// What the student booking pages share: topics, crisis lines, labels and dates.
import { atTime, dateOnly, formatDay, formatTime, sameDay } from '../guidanceHome/format';

export { apiError } from '../guidanceHome/format';

export const TOPICS = [
  { value: 'general', label: 'Something on my mind', hint: 'Not sure where to start? Pick this.' },
  { value: 'personal', label: 'Personal or family', hint: 'Friends, family, relationships, feelings' },
  { value: 'academic', label: 'School and studies', hint: 'Grades, workload, classes, teachers' },
  { value: 'career', label: 'Career or course choices', hint: 'What to take next, college, work' },
  { value: 'other', label: 'A question or request', hint: 'Forms, certificates, referrals, or anything not listed' },
];

export const RESULT_TOPICS = [
  { value: 'dass_followup', label: 'mood and stress' },
  { value: 'sdq_followup', label: 'strengths and difficulties' },
  { value: 'ryff_followup', label: 'well-being' },
  { value: 'riasec_followup', label: 'career interests' },
];

// Same words as the server's STUDENT_TOPICS (email_layout.py), which My sessions and emails show
export const topicLabel = (value) => {
  const topic = TOPICS.find((option) => option.value === value);
  if (topic) return topic.label;
  if (value === 'crisis') return 'Urgent support';
  const result = RESULT_TOPICS.find((option) => option.value === value);
  return result ? `My ${result.label} results` : 'Counseling';
};

/** A session's topic for the student: from its code when the server sends one. */
export const sessionTopic = (appointment) =>
  appointment.reason_code ? topicLabel(appointment.reason_code) : appointment.reason || 'Counseling';

export const ACTIVE = ['confirmed', 'pending', 'reschedule_requested'];
export const MAX_UPCOMING = 2;
const SESSION_MS = 60 * 60 * 1000;

export const startOf = (appointment) => new Date(appointment.start_at || `${appointment.date}T${appointment.time_slot}:00+08:00`);
export const endOf = (appointment) => new Date(appointment.end_at || startOf(appointment).getTime() + SESSION_MS);

/** "Today", "Tomorrow" or "Wed, Oct 8". */
export const dayLabel = (isoDate) => {
  const day = dateOnly(isoDate);
  const today = new Date();
  if (sameDay(day, today)) return 'Today';
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (sameDay(day, tomorrow)) return 'Tomorrow';
  return formatDay(day);
};

export const timeLabel = (isoDate, time) => formatTime(atTime(isoDate, time));
export const whenLabel = (isoDate, time) => `${dayLabel(isoDate)}, ${timeLabel(isoDate, time)}`;

export const isLink = (text) => /^https?:\/\//i.test(String(text || '').trim());

export const whereLabel = (appointment) => {
  if (appointment.mode === 'online') return appointment.location && !isLink(appointment.location) ? `Online · ${appointment.location}` : 'Online';
  return appointment.location ? `In person · ${appointment.location}` : 'In person, at the guidance office';
};

/** Sessions that hold a place and haven't started: what the upcoming-session limit counts (as the server does). */
export const notStarted = (appointments, now = new Date()) =>
  (appointments || []).filter((row) => ACTIVE.includes(row.status) && startOf(row) > now);

/** How a session stands, in the student's words, with a tone for its tag. */
export const studentStatus = (appointment, now = new Date()) => {
  const { status } = appointment;
  if (status === 'completed') return { label: 'Done', tone: 'is-done' };
  if (status === 'no_show') return { label: 'Missed', tone: 'is-muted', bookAgain: true };
  // The end time first: a request nobody answered before the time passed means it didn't happen
  if (status === 'reschedule_requested' && endOf(appointment) < now) {
    return { label: 'Didn’t happen', tone: 'is-muted', bookAgain: true };
  }
  if (status === 'cancelled') {
    const by = {
      student: appointment.late_cancellation ? 'You cancelled (late)' : 'You cancelled',
      counselor: 'Cancelled by your counselor',
      staff: 'Cancelled by the guidance office',
      closure: 'Cancelled · school closed',
      time_off: 'Cancelled · counselor away',
      counselor_removed: 'Cancelled · counselor left',
      account_closed: 'Cancelled',
    };
    return { label: by[appointment.cancelled_by_role] || 'Cancelled', tone: 'is-muted' };
  }
  if (status === 'reschedule_requested') return { label: 'Waiting for your counselor', tone: 'is-amber' };
  if (endOf(appointment) < now) return { label: 'Finished', tone: 'is-muted' };
  if (startOf(appointment) <= now) return { label: 'Happening now', tone: 'is-amber' };
  return { label: 'Confirmed', tone: 'is-green' };
};

// Reasons the system stores when nobody wrote one; never shown as if the counselor said them
const DEFAULT_REASONS = ['Reschedule request denied by counselor', 'Unable to accommodate at this time'];

/** Why a session was cancelled, when there is something worth saying. */
export const cancelledNote = (appointment) => {
  if (appointment.status !== 'cancelled') return '';
  if (appointment.cancelled_by_role === 'student') return '';
  const reason = String(appointment.cancellation_reason || '').trim();
  return DEFAULT_REASONS.includes(reason) ? '' : reason;
};
